/**
 * VCheckWpWizard — Geführter V-Check Schritt für Schritt (Phase 1 MVP)
 * ======================================================================
 * Iter89z-wiz (2026-02-26).
 *
 * Mobile-first Alternative zum klassischen Akkordeon-Formular
 * (`VCheckWpForm`). Zeigt genau EINEN Abschnitt pro Schritt mit großer
 * Fingerzielfläche, Fortschrittsbalken 1/13, native Handy-Kamera für Foto-
 * Blöcke und KI-Vorschläge für ausgewählte Fotos (Typenschild, Rohrmaß,
 * Zählerschrank, Türmaß).
 *
 * Technische Besonderheiten:
 * - Konditionale Schritt-Logik: Abschnitte mit NUR `show_if`-Feldern, die
 *   alle verborgen sind, werden übersprungen. Beispiel: Abschnitt
 *   "Solarthermie" wird komplett ausgeblendet, wenn `solar_thermal_present
 *   = false`.
 * - Alle sichtbaren Pflichtfelder werden am Ende zusammengefasst — die
 *   Unterschrift ist erst freigeschaltet, wenn alles erledigt ist.
 * - Toggle zur klassischen Alle-Felder-Ansicht wird vom Parent gesteuert.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, FileCheck, CheckCircle2, Sparkles,
  AlertTriangle, Camera, ListChecks, LayoutList, Download, Send,
  WifiOff, RefreshCw, Users, Zap, GitMerge,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import VCheckField from "./vcheck/VCheckField";
import VCheckSignatureBlock from "./vcheck/VCheckSignatureBlock";
import WizardPhotoBlock from "./vcheck/WizardPhotoBlock";
import WizardItem from "./vcheck/WizardItem";
import AiPanel from "./vcheck/AiPanel";
import PipeMeasureButton from "./vcheck/PipeMeasureButton";
import WizardOverview, { ProgressBar } from "./vcheck/WizardOverview";
import {
  visibleFields, itemStatus, sectionStats, overallStats, photoCount, effMinPhotos,
} from "./vcheck/wizardStatus";

const visibleSteps_ = (schema, data) =>
  (schema || []).filter((sec) => visibleFields(sec, data).length > 0);

// Aufeinanderfolgende Felder mit gleichem `group` → eine Karte (z. B. Rohrdaten KW / WW / Heizung).
function chunkFields(fields) {
  const out = [];
  for (const f of fields) {
    const last = out[out.length - 1];
    if (f.group) {
      if (last?.kind === "groups") {
        const g = last.groups[last.groups.length - 1];
        if (g.name === f.group) g.fields.push(f); else last.groups.push({ name: f.group, fields: [f] });
      } else out.push({ kind: "groups", groups: [{ name: f.group, fields: [f] }] });
    } else out.push({ kind: "field", field: f });
  }
  return out;
}

const PIPE_GROUPS = ["Kaltwasser", "Warmwasser", "Heizungswasser"];
const viewKey = (pid) => `vcheck_wiz_view_${pid}`;

export default function VCheckWpWizard({
  schema, doc, projectId, onFieldChange, onNaChange, onReload, onFinalize,
  onToggleClassicView, saving, finalizing,
  onOpenPdf, onOpenSend, pdfLoading, offline,
  live, conflicts, onResolveConflict, onAiDecide, onRunAiFull, aiRunning,
}) {
  const [{ stepIdx, overview }, setView] = useState(() => {
    try {
      const v = JSON.parse(sessionStorage.getItem(viewKey(projectId)) || "null");
      if (v && typeof v.stepIdx === "number") return v;
    } catch { /* noop */ }
    return { stepIdx: 0, overview: true };
  });
  const setStepIdx = (fnOrIdx) => setView((v) => ({
    overview: false,
    stepIdx: typeof fnOrIdx === "function" ? fnOrIdx(v.stepIdx) : fnOrIdx,
  }));
  const showOverview = () => setView((v) => ({ ...v, overview: true }));
  useEffect(() => {
    try { sessionStorage.setItem(viewKey(projectId), JSON.stringify({ stepIdx, overview })); } catch { /* noop */ }
  }, [projectId, stepIdx, overview]);

  const data = doc?.data || {};
  const photos = doc?.photos || {};
  const pendingFocus = useRef(null);

  // Swipe-Gesten links/rechts (horizontal > 60 px, vertikal < 40 px)
  const swipeStart = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    swipeStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    const t = e.changedTouches?.[0];
    if (!start || !t || overview) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 60 || Math.abs(dy) > 40 || Date.now() - start.t > 600) return;
    if (dx < 0) goNext(); else goPrev();
  };

  const visibleSteps = useMemo(() => visibleSteps_(schema, data), [schema, data]);
  const overall = useMemo(() => overallStats(visibleSteps, doc), [visibleSteps, doc]);

  useEffect(() => {
    if (stepIdx >= visibleSteps.length && visibleSteps.length) setStepIdx(visibleSteps.length - 1);
  }, [visibleSteps.length, stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentSection = visibleSteps[stepIdx];
  const goPrev = () => setStepIdx((i) => Math.max(0, i - 1));
  const goNext = () => setStepIdx((i) => Math.min(visibleSteps.length - 1, i + 1));
  const jumpTo = (idx, fieldKey) => { pendingFocus.current = fieldKey || null; setStepIdx(idx); };

  const openItems = useMemo(() => {
    const items = [];
    for (const sec of visibleSteps) {
      for (const f of sectionStats(sec, doc).openFields) {
        items.push({
          sectionKey: sec.section, sectionTitle: sec.title, field: f, kind: f.type === "photo_block" ? "photo" : "field",
          detail: f.type === "photo_block" ? `${photoCount(f, doc)}/${effMinPhotos(f, data)} Fotos` : undefined,
        });
      }
    }
    return items;
  }, [visibleSteps, doc, data]);
  const canSign = openItems.length === 0;

  // Beim Schritt-/Ansichtswechsel nach oben bzw. zum angesprungenen Feld scrollen
  const rootRef = useRef(null);
  useEffect(() => {
    const key = pendingFocus.current;
    pendingFocus.current = null;
    requestAnimationFrame(() => {
      const target = key ? document.querySelector(`[data-item-key="${key}"]`) : rootRef.current;
      const top = target?.getBoundingClientRect().top;
      if (top != null && (key || top < 0)) window.scrollTo({ top: window.scrollY + top - (window.innerWidth < 1280 ? 72 : 135), behavior: "smooth" });
    });
  }, [stepIdx, overview]);

  if (!overview && !currentSection) {
    return <div className="p-6 text-center text-zinc-500">Lade Wizard …</div>;
  }

  const isLastStep = stepIdx >= visibleSteps.length - 1;

  const renderItem = (f, compact = false) => {
    const status = itemStatus(f, doc);
    if (f.type === "photo_block") {
      return (
        <WizardItem key={f.key} field={f} status={status} naInfo={doc?.na?.[f.key]} onNa={onNaChange}
                    suggestion={doc?.ai_suggestions?.[f.key]} aiApplied={doc?.ai_applied?.[f.key]} onAiDecide={onAiDecide}>
          <WizardPhotoBlock
            field={f} embedded
            photos={photos[f.key] || []}
            sketches={f.floorplan ? (doc?.floorplan_sketches || []) : []}
            minPhotos={effMinPhotos(f, data)}
            projectId={projectId}
            onChanged={onReload}
            onApplySuggestions={(sugg) => applyAiSuggestions(sugg, f, onFieldChange)}
            missing={status === "open"}
            offline={offline}
          />
        </WizardItem>
      );
    }
    return (
      <WizardItem key={f.key} field={f} status={status} naInfo={doc?.na?.[f.key]} compact={compact}
                  onNa={f.readonly ? null : onNaChange}
                  suggestion={doc?.ai_suggestions?.[f.key]} aiApplied={doc?.ai_applied?.[f.key]} onAiDecide={onAiDecide}>
        <VCheckField field={f} value={data[f.key]} onChange={(v) => onFieldChange(f.key, v)}
                     missing={status === "open"} projectId={projectId} onVoiceApplied={onReload} />
      </WizardItem>
    );
  };

  return (
    <div className="space-y-4" data-testid="vcheck-wp-wizard" ref={rootRef}
         onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <WizardHeader
        overview={overview} section={currentSection} stepIdx={stepIdx} total={visibleSteps.length}
        overall={overall} live={live} saving={saving} offline={offline}
        projectId={projectId} onReload={onReload} onOpenPdf={onOpenPdf} pdfLoading={pdfLoading}
        onOpenSend={onOpenSend} onToggleClassicView={onToggleClassicView} onShowOverview={showOverview}
      />
      <StepChips steps={visibleSteps} doc={doc} stepIdx={stepIdx} overview={overview}
                 onOverview={showOverview} onJump={jumpTo} />

      {overview ? (
        <div className="pb-24 space-y-4">
          <AiPanel steps={visibleSteps} doc={doc} aiRunning={aiRunning} onRunFull={onRunAiFull}
                   onAiDecide={onAiDecide} onJump={jumpTo} />
          <WizardOverview steps={visibleSteps} doc={doc} overall={overall} onJump={jumpTo} />
        </div>
      ) : (
        <div className="space-y-3 pb-24" data-testid={`wizard-step-${currentSection.section}`}>
          {chunkFields(visibleFields(currentSection, data).filter((f) => f.type !== "signature")).map((c, i) => (
            c.kind === "field" ? renderItem(c.field) : (
              <div key={`g-${i}`} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {c.groups.map((g) => {
                  const ok = g.fields.every((f) => ["done", "na"].includes(itemStatus(f, doc)));
                  return (
                    <div key={g.name} data-testid={`wizard-group-${g.name}`}
                         className={`rounded-2xl border-2 p-3 space-y-2 ${ok ? "border-emerald-500/50" : "border-red-500/40"}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className={`text-[15px] font-bold ${ok ? "text-emerald-300" : "text-red-300"}`}>{g.name}</div>
                        {PIPE_GROUPS.includes(g.name) && <PipeMeasureButton projectId={projectId} group={g.name} onDone={onReload} />}
                      </div>
                      {g.fields.map((f) => renderItem(f, true))}
                    </div>
                  );
                })}
              </div>
            )
          ))}

          {isLastStep && (
            <SummaryAndSign
              openItems={openItems} canSign={canSign} doc={doc} projectId={projectId}
              onReload={onReload} onFinalize={onFinalize} finalizing={finalizing}
              onJumpToStep={(sectionKey, fieldKey) => {
                const idx = visibleSteps.findIndex((s) => s.section === sectionKey);
                if (idx >= 0) jumpTo(idx, fieldKey);
              }}
            />
          )}
        </div>
      )}

      <div data-bottom-bar data-testid="wizard-bottom-nav" className="fixed bottom-0 left-0 right-0 bg-[#09090B]/95 backdrop-blur border-t border-zinc-800 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] z-20 flex items-center justify-between gap-3">
        <button
          onClick={overview ? undefined : goPrev}
          disabled={overview || stepIdx === 0}
          data-testid="wizard-prev-btn"
          className="h-12 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[14px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-30"
        >
          <ChevronLeft size={18} /> Zurück
        </button>
        <button onClick={showOverview} data-testid="wizard-nav-overview"
                className="text-[12px] text-zinc-400 font-mono text-center leading-tight">
          <span className={`block text-[15px] font-bold ${overall.open ? "text-red-300" : "text-emerald-300"}`}>{overall.pct} %</span>
          {overview ? "Übersicht" : `${stepIdx + 1} / ${visibleSteps.length}`}
        </button>
        <button
          onClick={overview ? () => setStepIdx(stepIdx) : goNext}
          disabled={!overview && isLastStep}
          data-testid="wizard-next-btn"
          className="h-12 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[14px] font-bold inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-30"
        >
          {overview ? "Los geht's" : "Weiter"} <ChevronRight size={18} />
        </button>
      </div>

      {conflicts && conflicts.length > 0 && (
        <ConflictModal conflicts={conflicts} onResolve={onResolveConflict} />
      )}
    </div>
  );
}

function WizardHeader({
  overview, section, stepIdx, total, overall, live, saving, offline, projectId, onReload,
  onOpenPdf, pdfLoading, onOpenSend, onToggleClassicView, onShowOverview,
}) {
  const btn = "h-9 px-3 rounded-lg text-[12.5px] font-semibold inline-flex items-center gap-1.5 flex-shrink-0";
  return (
    <div className="space-y-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-emerald-300 uppercase tracking-wider inline-flex items-center gap-1.5">
            <FileCheck size={13} /> {overview ? "Übersicht V-Check" : `Schritt ${stepIdx + 1} von ${total}`}
          </div>
          <div className="text-[19px] font-bold text-zinc-50 leading-tight mt-0.5" data-testid="wizard-step-title">
            {overview ? "Was ist erledigt, was fehlt noch?" : section?.title}
          </div>
        </div>
        <button onClick={onShowOverview} data-testid="wizard-header-pct"
                className={`flex-shrink-0 h-14 w-16 rounded-xl border-2 flex flex-col items-center justify-center ${overall.open ? "border-red-500/50 bg-red-500/10" : "border-emerald-500/60 bg-emerald-500/10"}`}>
          <span className={`text-[17px] font-bold tabular-nums ${overall.open ? "text-red-200" : "text-emerald-200"}`}>{overall.pct}%</span>
          <span className="text-[10px] text-zinc-400">{overall.open} offen</span>
        </button>
      </div>
      <ProgressBar pct={overall.pct} />
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-0.5">
        <button onClick={onShowOverview} data-testid="wizard-overview-btn"
                className={`${btn} ${overview ? "bg-emerald-500 text-zinc-950" : "bg-zinc-800 text-zinc-100"}`}>
          <ListChecks size={14} /> Übersicht
        </button>
        <PvAutofillButton projectId={projectId} onApplied={onReload} />
        {onOpenPdf && (
          <button onClick={() => onOpenPdf(false)} disabled={pdfLoading} data-testid="vcheck-pdf-btn"
                  className={`${btn} bg-zinc-800 text-zinc-100 disabled:opacity-50`}>
            {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : <FileCheck size={13} />} PDF
          </button>
        )}
        {onOpenSend && (
          <button onClick={onOpenSend} data-testid="vcheck-send-installer-btn"
                  className={`${btn} bg-blue-500/15 border border-blue-500/40 text-blue-300`}>
            <Send size={13} /> Senden
          </button>
        )}
        <button onClick={onToggleClassicView} data-testid="vcheck-toggle-classic"
                className={`${btn} bg-zinc-800 text-zinc-100`}>
          <LayoutList size={14} /> Alle Felder
        </button>
        {live?.presence?.length > 1 && (
          <span data-testid="vcheck-presence" title={live.presence.map((u) => u.name).join(", ")}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[11px] font-bold flex-shrink-0">
            <Users size={11} /> {live.presence.length} online
          </span>
        )}
        {saving && (
          <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1 flex-shrink-0">
            <Loader2 size={11} className="animate-spin" />Speichere…
          </span>
        )}
      </div>
      {offline && (!offline.online || offline.pendingCount.patches > 0 || offline.pendingCount.photos > 0) && (
        <OfflineBanner offline={offline} />
      )}
    </div>
  );
}

function OfflineBanner({ offline }) {
  const n = offline.pendingCount.patches + offline.pendingCount.photos;
  return (
    <div data-testid="vcheck-offline-banner"
         className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[12px] ${
           offline.online ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200" : "bg-amber-500/10 border-amber-500/40 text-amber-200"}`}>
      {offline.syncing ? (
        <><RefreshCw size={12} className="animate-spin" /><span>Synchronisiere Offline-Daten …</span></>
      ) : offline.online ? (
        <>
          <RefreshCw size={12} /><span><b>{n}</b> Einträge warten auf Upload</span>
          <button onClick={() => offline.sync()} data-testid="vcheck-offline-sync-btn"
                  className="ml-auto h-7 px-2 rounded bg-emerald-500/20 text-emerald-200 font-semibold text-[11px]">
            Jetzt synchronisieren
          </button>
        </>
      ) : (
        <><WifiOff size={12} /><span><b>Offline</b> — Daten liegen lokal{n > 0 && ` · ${n} in Warteschlange`}. Sync bei Verbindung.</span></>
      )}
    </div>
  );
}

function StepChips({ steps, doc, stepIdx, overview, onOverview, onJump }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 py-1 border-b border-zinc-800 pb-3" data-testid="wizard-step-chips">
      <button onClick={onOverview} data-testid="wizard-step-chip-overview"
              className={`h-10 px-3 rounded-lg text-[12px] font-bold flex-shrink-0 inline-flex items-center gap-1 ${
                overview ? "bg-zinc-100 text-zinc-950" : "bg-zinc-800 text-zinc-200"}`}>
        <ListChecks size={13} /> Alle
      </button>
      {steps.map((sec, i) => {
        const st = sectionStats(sec, doc);
        const ok = st.open === 0;
        const active = !overview && i === stepIdx;
        return (
          <button key={sec.section} onClick={() => onJump(i)} title={sec.title}
                  data-testid={`wizard-step-chip-${sec.section}`} data-done={ok ? "true" : "false"}
                  className={`h-10 min-w-[44px] px-2 rounded-lg flex-shrink-0 flex flex-col items-center justify-center leading-none border ${
                    ok ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200" : "bg-red-500/15 border-red-500/50 text-red-200"
                  } ${active ? "ring-2 ring-white ring-offset-2 ring-offset-[#09090B]" : ""}`}>
            <span className="text-[12px] font-bold font-mono">{sec.section}</span>
            <span className="text-[9.5px] mt-0.5 tabular-nums">{st.pct}%</span>
          </button>
        );
      })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// PV-Autofill-Button (Iter89z-wiz3)
// ---------------------------------------------------------------------------
function PvAutofillButton({ projectId, onApplied }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/vcheck-wp/autofill-from-pv`);
      const n = Object.keys(r.data?.applied || {}).length;
      if (n === 0) {
        if (r.data?.source === "none") {
          toast.info("Keine PV-Konfiguration gefunden");
        } else {
          toast.info("Felder bereits ausgefüllt — kein Update nötig");
        }
      } else {
        toast.success(`${n} Felder aus PV-Anlage übernommen`);
        onApplied?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "PV-Autofill fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={loading}
      data-testid="vcheck-pv-autofill-btn"
      className="flex-shrink-0 h-9 px-2.5 rounded-lg bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/40 text-amber-200 text-[11.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
      title="kWp + Wechselrichter aus bestehender PV-Anlage übernehmen"
    >
      {loading ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
      PV-Daten
    </button>
  );
}


// ---------------------------------------------------------------------------
// Konflikt-Modal — zeigt pro Key Server-Wert ↔ eigenen Wert, Nutzer entscheidet
// ---------------------------------------------------------------------------
function ConflictModal({ conflicts, onResolve }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="vcheck-conflict-modal">
      <div className="bg-[#09090B] border-2 border-amber-400/60 rounded-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#09090B] px-5 py-3 border-b border-zinc-800">
          <div className="inline-flex items-center gap-1.5 text-amber-200 font-bold text-[14px]">
            <GitMerge size={14} /> {conflicts.length} Konflikt{conflicts.length === 1 ? "" : "e"} entdeckt
          </div>
          <div className="text-[11.5px] text-zinc-400 mt-1">
            Jemand anderes hat denselben Wert geändert, während du offline oder in Bearbeitung warst. Entscheide pro Feld.
          </div>
        </div>
        <div className="p-5 space-y-3">
          {conflicts.map((c) => (
            <div key={c.key} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 space-y-2" data-testid={`conflict-${c.key}`}>
              <div className="flex items-center justify-between gap-2">
                <code className="text-[11px] font-mono text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded">{c.key}</code>
                <span className="text-[10.5px] text-zinc-500">Server {new Date(c.server_ts).toLocaleTimeString("de-DE")}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-2">
                  <div className="text-[10px] uppercase font-bold text-emerald-300 mb-1">Server-Wert</div>
                  <div className="text-[12px] text-zinc-100 font-mono break-words">
                    {c.server_value === null || c.server_value === undefined || c.server_value === ""
                      ? <em className="text-zinc-500">— leer —</em>
                      : String(c.server_value)}
                  </div>
                </div>
                <div className="rounded-lg bg-sky-500/10 border border-sky-500/40 p-2">
                  <div className="text-[10px] uppercase font-bold text-sky-300 mb-1">Dein Wert</div>
                  <div className="text-[12px] text-zinc-100 font-mono break-words">
                    {c.client_value === null || c.client_value === undefined || c.client_value === ""
                      ? <em className="text-zinc-500">— leer —</em>
                      : String(c.client_value)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onResolve(c.key, true)}
                  data-testid={`conflict-keep-server-${c.key}`}
                  className="flex-1 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[12px] font-bold"
                >
                  Server behalten
                </button>
                <button
                  type="button"
                  onClick={() => onResolve(c.key, false)}
                  data-testid={`conflict-keep-mine-${c.key}`}
                  className="flex-1 h-9 rounded-lg bg-sky-500 hover:bg-sky-400 text-zinc-950 text-[12px] font-bold"
                >
                  Meinen Wert durchsetzen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Zusammenfassung + Unterschrift (letzter Step)
// ---------------------------------------------------------------------------
function SummaryAndSign({
  openItems, canSign, doc, projectId, onReload, onFinalize, finalizing, onJumpToStep,
}) {
  const sigField = {
    label: "Unterschrift Kunde",
    hint: "Digital auf Tablet/Handy signieren oder Foto der Papier-Unterschrift hochladen",
  };

  return (
    <div className="space-y-3">
      <div className={`rounded-xl border p-4 ${
        canSign ? "bg-emerald-500/[0.05] border-emerald-500/40"
                : "bg-amber-500/[0.05] border-amber-500/40"
      }`}>
        <div className="flex items-start gap-2.5 mb-2">
          {canSign
            ? <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" />
            : <AlertTriangle size={18} className="text-amber-400 flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[13.5px] text-zinc-100">
              {canSign
                ? "Alle Pflichtangaben vollständig"
                : `Noch ${openItems.length} offene Pflichtangabe${openItems.length === 1 ? "" : "n"}`}
            </div>
            <div className="text-[11.5px] text-zinc-400 mt-0.5">
              {canSign
                ? "Die Unterschrift kann jetzt eingeholt werden."
                : "Diese Punkte müssen vor der Kundenunterschrift ergänzt werden."}
            </div>
          </div>
        </div>

        {openItems.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="wizard-open-items">
            {openItems.map((it, idx) => (
              <li key={`${it.sectionKey}-${it.field.key}-${idx}`}>
                <button
                  type="button"
                  onClick={() => onJumpToStep(it.sectionKey, it.field.key)}
                  className="w-full text-left p-2 rounded-md bg-zinc-900/60 hover:bg-zinc-800 inline-flex items-center gap-2 border border-zinc-800"
                  data-testid={`wizard-open-item-${it.field.key}`}
                >
                  <span className="text-[10.5px] font-mono bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded">
                    {it.sectionKey}
                  </span>
                  <span className="text-[12px] text-zinc-200 flex-1 truncate">{it.field.label}</span>
                  {it.detail && (
                    <span className="text-[10.5px] text-zinc-500 font-mono">{it.detail}</span>
                  )}
                  <ChevronRight size={13} className="text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <VCheckSignatureBlock field={sigField} doc={doc} projectId={projectId} onChanged={onReload} />

      <button
        onClick={onFinalize}
        disabled={finalizing || doc.finalized_at || !canSign}
        data-testid="wizard-finalize-btn"
        className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {finalizing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
        {doc.finalized_at ? "Bereits technisch freigegeben" : "Technisch freigeben"}
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// KI-Vorschläge aus Vision-Analyse auf Formular-Felder mappen
// ---------------------------------------------------------------------------
// Mapping ist abhängig vom Vision-Modus. Bewusst konservativ: Werte werden
// nur gesetzt, wenn das Feld aktuell leer ist (keine Überschreibung).
function applyAiSuggestions(suggestions, field, onFieldChange) {
  if (!suggestions || typeof suggestions !== "object") return;

  const mode = field.ai_vision;
  let appliedCount = 0;

  const setIf = (key, value) => {
    if (value === null || value === undefined || value === "") return;
    onFieldChange(key, value);
    appliedCount += 1;
  };

  if (mode === "nameplate") {
    setIf("existing_manufacturer",
          [suggestions.manufacturer, suggestions.model].filter(Boolean).join(" "));
    setIf("existing_year", suggestions.year);
    setIf("existing_power_kw", suggestions.nominal_power_kw);
  } else if (mode === "pipe_dimension") {
    setIf("hk1_pipe_dn", suggestions.pipe_dn);
    setIf("hk1_pipe_material", suggestions.pipe_material);
  } else if (mode === "fuse_box") {
    setIf("supply_phases", suggestions.supply_phases);
    setIf("supply_ampere", suggestions.supply_ampere);
    setIf("cabinet_status", suggestions.cabinet_status);
  } else if (mode === "door_measurement") {
    setIf("narrow_width_cm", suggestions.width_cm);
    setIf("narrow_height_cm", suggestions.height_cm);
  } else if (mode === "outdoor_unit_environment") {
    // KI gibt Werte in cm → bestehende Schema-Felder sind in m. Konvertieren.
    const cmToM = (cm) => (typeof cm === "number" && cm > 0 ? Math.round((cm / 100) * 100) / 100 : null);
    setIf("distance_window_m", cmToM(suggestions.distance_to_window_cm));
    setIf("distance_border_m", cmToM(suggestions.distance_to_property_line_cm));
    // Nachbarschlafraum nur setzen, wenn KI das als besonders nah erkannt hat
    if (suggestions.noise_sensitive_nearby === true) {
      // konservativ: nur anzeigen, Wert bleibt User-Entscheidung — kein Autoset
    }
    // Untergrund-Mapping KI ↔ Schema-Options
    const groundMap = {
      "Betonplatte": "Beton", "Betonsockel": "Beton", "Kies": "Kies",
      "Rasen": "Erde", "Terrasse": "Pflaster", "Pflaster": "Pflaster",
    };
    if (suggestions.ground_type && groundMap[suggestions.ground_type]) {
      setIf("outdoor_ground", groundMap[suggestions.ground_type]);
    }
  }

  if (appliedCount > 0) {
    toast.success(`${appliedCount} Vorschläge übernommen`);
  } else {
    toast.info("Keine neuen Werte zum Übernehmen");
  }
}
